import moment from 'moment';
import { timeOnlyShortFormatter } from './formatDateTime';

// Time ranges definition in UTC (12:00 to 03:00 next day)
const quantumRanges = {
  1: [[12, 3]], // Monday 12:00 UTC to Tuesday 03:00 UTC
  2: [[12, 3]], // Tuesday 12:00 UTC to Wednesday 03:00 UTC
  3: [[12, 3]], // Wednesday 12:00 UTC to Thursday 03:00 UTC
  4: [[12, 3]], // Thursday 12:00 UTC to Friday 03:00 UTC
  5: [[12, 3]], // Friday 12:00 UTC to Saturday 03:00 UTC
};

// Helper: Create a Date object at a given UTC hour (for formatting)
export const createDateFromUTCHour = (hourInUtc) => {
  const now = new Date();
  return new Date(Date.UTC(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hourInUtc,
    0,
    0,
  ));
};

// Helper: Convert an hour (e.g. 12) into seconds
const convertToSeconds = (hour) => hour * 3600;

// Prepare an array of timeRanges for easier lookup.
// Each item has a day (number), a start time (in seconds), and an end time (in seconds).
const timeRanges = Object.entries(quantumRanges).map(([day, ranges]) => ({
  day: parseInt(day, 10),
  start: convertToSeconds(ranges[0][0]), // e.g. 12:00 UTC = 12 * 3600 seconds
  end: convertToSeconds(ranges[0][1]), // e.g. 03:00 UTC = 3 * 3600 seconds
}));

/**
 * Determines if the current UTC time is within one of the quantum windows.
 * Quantum windows are defined as:
 *   - Monday:    12:00 UTC to Tuesday 03:00 UTC
 *   - Tuesday:   12:00 UTC to Wednesday 03:00 UTC
 *   - Wednesday: 12:00 UTC to Thursday 03:00 UTC
 *   - Thursday:  12:00 UTC to Friday 03:00 UTC
 *   - Friday:    12:00 UTC to Saturday 03:00 UTC
 *
 * Note: On Monday we do not wrap-around and check Friday's window.
 */
export const isInTimeRange = () => {
  const nowUtc = moment.utc();
  const currentDay = nowUtc.day(); // moment.utc().day(): 0 = Sunday, 1 = Monday, etc.
  const currentTime = nowUtc.hours() * 3600 + nowUtc.minutes() * 60 + nowUtc.seconds();

  // Only check the previous day's window if today is NOT Monday.
  // (This prevents wrapping from Monday to Friday.)
  if (currentDay !== 1) {
    const previousDay = currentDay - 1;
    const previousRange = timeRanges.find((r) => r.day === previousDay);
    // If we have a defined window for the previous day and the current time is before that window's end,
    // we are still in the previous day's quantum window.
    if (previousRange && currentTime <= previousRange.end) {
      return true;
    }
  }

  // Check if we're in today's window (i.e. after the start time).
  const todayRange = timeRanges.find((r) => r.day === currentDay);
  if (todayRange && currentTime >= todayRange.start) {
    return true;
  }

  return false;
};

// A helper alias for isInTimeRange.
export const isInQuantumWindow = () => isInTimeRange();

/**
 * Creates an array of formatted strings describing the quantum windows.
 * For example: "Mon: 12:00 PM - 03:00 AM"
 */
export const createQuantumWindowsMessage = () => {
  const timeRangeStrings = [];

  Object.entries(quantumRanges).forEach(([day, ranges]) => {
    ranges.forEach(([startHour, endHour]) => {
      // Map day (1-5) to weekday abbreviations (Mon-Fri)
      const dayOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'][parseInt(day, 10) - 1];
      timeRangeStrings.push(
        `${dayOfWeek}: ${timeOnlyShortFormatter(createDateFromUTCHour(startHour))} - ${timeOnlyShortFormatter(createDateFromUTCHour(endHour))}`,
      );
    });
  });

  return timeRangeStrings;
};

/**
 * Calculates the time remaining until the next quantum window starts.
 * For example, if now is Tuesday 03:01 UTC, it returns roughly 09:00:00 until Tuesday 12:00 UTC.
 * If the current time is Saturday (outside any window), it calculates the hours until Monday 12:00 UTC.
 *
 * Returns an array formatted as [hours, minutes, seconds] (each as two-digit strings).
 */
export const timeUntilNextRange = () => {
  const nowUtc = moment.utc();
  const currentDay = nowUtc.day();
  const currentTime = nowUtc.hours() * 3600 + nowUtc.minutes() * 60 + nowUtc.seconds();

  // Check if today's window hasn't started yet (i.e., we're offline but still in the same day).
  for (const range of timeRanges) {
    if (currentDay === range.day && currentTime < range.start) {
      const secondsUntil = range.start - currentTime;
      const duration = moment.duration(secondsUntil, 'seconds');
      return [
        `${Math.floor(duration.asHours())}`.padStart(2, '0'),
        `${duration.minutes()}`.padStart(2, '0'),
        `${duration.seconds()}`.padStart(2, '0'),
      ];
    }
  }

  // Otherwise, find the next available window in the coming days.
  let daysToAdd = 1;
  let nextDay = (currentDay + 1) % 7;
  while (daysToAdd <= 7) {
    const targetDay = nextDay; // Capture `nextDay` to avoid ESLint error

    const range = timeRanges.find((r) => r.day === targetDay);
    if (range) {
      // Calculate the total seconds until the next window starts.
      // This takes into account the remaining seconds in the current day,
      // plus full days until the target day, plus the target day's start time.
      const secondsUntil = daysToAdd * 24 * 3600 - currentTime + range.start;
      const duration = moment.duration(secondsUntil, 'seconds');
      return [
        `${Math.floor(duration.asHours())}`.padStart(2, '0'),
        `${duration.minutes()}`.padStart(2, '0'),
        `${duration.seconds()}`.padStart(2, '0'),
      ];
    }
    daysToAdd += 1;
    nextDay = (nextDay + 1) % 7;
  }

  // Fallback: if no range is found in the next week, calculate using the first range of the next week.
  const firstRange = timeRanges[0];
  const nextWeekStart = moment.utc()
    .add(1, 'weeks')
    .startOf('isoWeek')
    .day(firstRange.day)
    .add(firstRange.start, 'seconds');

  const duration = moment.duration(nextWeekStart.diff(nowUtc));
  return [
    `${Math.floor(duration.asHours())}`.padStart(2, '0'),
    `${duration.minutes()}`.padStart(2, '0'),
    `${duration.seconds()}`.padStart(2, '0'),
  ];
};
